<script setup>
import { ref, reactive, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import { formatarDataBR } from '@/utils/useDataBR'
import { FIN_ST_MOV_BANC } from '@/constants/FIN_ST_MOV_BANC'

const emit = defineEmits(['adicionar-item', 'editar-item', 'visualizar-item'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)
const listas = reactive({})
const isLoadingListas = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'id', label: 'ID', width: 70, sortable: true, slotName: 'idMovBanc' },
  { field: 'dataMovimento', label: 'Data Movimento', sortable: true, slotName: 'dataMovimento' },
  { field: 'nomeContaBancaria', label: 'Conta Bancária', sortable: true, slotName: 'contaBancaria' },
  { field: 'nomeParceiroTitulo', label: 'Título / Parceiro', sortable: true, slotName: 'tituloParceiro' },
  { field: 'lstTipoMovimentacao', label: 'Tipo', align: 'center', sortable: true, slotName: 'tipo' },
  { field: 'lstStatusMovBancaria', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'valorEfetivoMoedaBanco', label: 'Valor Efetivo', align: 'right', class: 'fw-bold', sortable: true, slotName: 'valorEfetivo' },
  { field: 'acoes', label: 'Ações', width: 100, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstTipoMovimentacao: (row) => getTextoLista('LstTipoMovimentacao', row.lstTipoMovimentacao),
    lstStatusMovBancaria: (row) => obterTextoStatus(row.lstStatusMovBancaria)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

const filtros = reactive({
  idTituloFinanceiro: '',
  idContaBancaria: '',
  dataInicio: '',
  dataFim: ''
})

const getTextoLista = (listaKey, id) => {
  if (!id) return '-'
  const lista = listas[listaKey] || []
  const item = lista.find(i =>
    String(i.idListaSistemaItem) === String(id) ||
    String(i.idListaClienteItem) === String(id)
  )
  return item ? item.textoLista : id
}

const formatarMoedaPorLista = (valor, codigoMoeda) => {
  if (valor == null) return '-'
  try {
    const moeda = (listas.LstMoedaBanco || []).find(m =>
      String(m.idListaSistemaItem) === String(codigoMoeda) ||
      String(m.idListaClienteItem) === String(codigoMoeda)
    )
    const texto = moeda?.textoLista || codigoMoeda || 'BRL'
    const isoCode = texto.replace(/^[A-Z]+_/, '').match(/^[A-Z]{3}/)?.[0] || 'BRL'
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: isoCode }).format(Number(valor))
  } catch (e) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(valor))
  }
}

const obterClasseStatus = (status) => {
  if (status === FIN_ST_MOV_BANC.Criada) return 'badge-ghost-warning'
  if (status === FIN_ST_MOV_BANC.Baixada) return 'badge-ghost-info'
  if (status === FIN_ST_MOV_BANC.Conciliada) return 'badge-ghost-success'
  if (status === FIN_ST_MOV_BANC.Estornada) return 'badge-ghost-danger'
  return 'badge-ghost-secondary'
}

const obterTextoStatus = (status) => {
  if (status === FIN_ST_MOV_BANC.Criada) return 'Criada'
  if (status === FIN_ST_MOV_BANC.Baixada) return 'Baixada'
  if (status === FIN_ST_MOV_BANC.Conciliada) return 'Conciliada'
  if (status === FIN_ST_MOV_BANC.Estornada) return 'Estornada'
  return status || '-'
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.idTituloFinanceiro) params.append('idTituloFinanceiro', filtros.idTituloFinanceiro)
    if (filtros.idContaBancaria) params.append('idContaBancaria', filtros.idContaBancaria)
    if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio)
    if (filtros.dataFim) params.append('dataFim', filtros.dataFim)

    const response = await api.get(`/MovimentacoesBancarias?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar movimentações bancárias:', error)
    erro.value = 'Não foi possível carregar as movimentações bancárias.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['MovimentacaoBancaria'])
    Object.assign(listas, result)
  } catch (error) {
    console.error('Erro ao carregar listas auxiliares:', error)
  } finally {
    isLoadingListas.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => {
  filtros.idTituloFinanceiro = ''
  filtros.idContaBancaria = ''
  filtros.dataInicio = ''
  filtros.dataFim = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarNovo = () => { emit('adicionar-item') }

const visualizarRegistro = (item) => { emit('visualizar-item', item) }

const excluirRegistro = async (item) => {
  if (item.lstStatusMovBancaria === FIN_ST_MOV_BANC.Conciliada) {
    alert('Não é possível estornar uma movimentação que já foi conciliada.')
    return
  }
  if (item.lstStatusMovBancaria === FIN_ST_MOV_BANC.Estornada) {
    alert('Esta movimentação já foi estornada.')
    return
  }
  if (confirm(`Tem certeza que deseja estornar a movimentação ID ${item.idMovimentacaoBancaria}?`)) {
    try {
      await api.delete(`/MovimentacoesBancarias/${item.idMovimentacaoBancaria}`)
      buscarDados(paginacao.value.CurrentPage)
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao estornar movimentação.')
    }
  }
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Movimentações Bancárias"
    subtitulo="Extrato e baixas de títulos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    mostrar-botao-adicionar
    label-botao-adicionar="Nova Movimentação"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarNovo"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label class="form-label">Data Início</label>
          <input type="date" class="form-control" v-model="filtros.dataInicio">
        </div>
        <div class="col-md-3">
          <label class="form-label">Data Fim</label>
          <input type="date" class="form-control" v-model="filtros.dataFim">
        </div>
        <div class="col-md-3">
          <label class="form-label">ID Título</label>
          <input type="text" class="form-control" v-model="filtros.idTituloFinanceiro" placeholder="Filtrar por título">
        </div>
        <div class="col-md-3 text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma movimentação bancária encontrada."
      row-key="idMovimentacaoBancaria"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="visualizarRegistro($event.row)"
    >
      <template #idMovBanc="{ row }">
        <span class="text-muted fw-medium">#{{ row.idMovimentacaoBancaria }}</span>
      </template>

      <template #dataMovimento="{ row }">
        <span class="fw-medium">{{ formatarDataBR(row.dataMovimento) }}</span>
      </template>

      <template #contaBancaria="{ row }">
        {{ row.nomeContaBancaria || 'Baixa Virtual' }}
      </template>

      <template #tituloParceiro="{ row }">
        <span class="d-block fw-medium">{{ row.nomeParceiroTitulo || 'Tarifa / Avulsa' }}</span>
        <small v-if="row.idTituloFinanceiro" class="text-muted">Título: {{ row.idTituloFinanceiro }}</small>
      </template>

      <template #tipo="{ row }">
        <span class="badge badge-ghost-secondary">
          {{ getTextoLista('LstTipoMovimentacao', row.lstTipoMovimentacao) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatus(row.lstStatusMovBancaria)">
          {{ obterTextoStatus(row.lstStatusMovBancaria) }}
        </span>
      </template>

      <template #valorEfetivo="{ row }">
        <span class="fw-bold">
          {{ formatarMoedaPorLista(row.valorEfetivoMoedaBanco, row.lstMoedaBanco) }}
          <small class="text-muted d-block" style="font-size:0.7rem; line-height:1.2;">
            {{ getTextoLista('LstMoedaBanco', row.lstMoedaBanco) }}
          </small>
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button class="btn-icon" @click="visualizarRegistro(row)" title="Visualizar Extrato Detalhado">
            <i class="bi bi-eye-fill text-primary"></i>
          </button>
          <button
            v-if="row.lstStatusMovBancaria === FIN_ST_MOV_BANC.Baixada || row.lstStatusMovBancaria === FIN_ST_MOV_BANC.Criada"
            class="btn-icon"
            @click="excluirRegistro(row)"
            title="Estornar"
          >
            <i class="bi bi-arrow-counterclockwise text-danger"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>
